import React, { useCallback, useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import InputLabel from "@mui/material/InputLabel";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ip_back } from "../../config";
import { ExerciseCard } from "../ExerciseCard";
import {
  grpplayerlist,
  tablelist,
  exdetails,
} from "../funtionsBD/Mainservice";
import { normalizeAppLang } from "../../utils/appLanguages";
import { isPersonalizedTypeName } from "../../utils/personalizedTrainingSlot";
import { blockTypeHeadline } from "../../utils/planningSubgroups";
import {
  CLASSEMENTS,
  levelSummaryLabel,
  blockedReasonLabel,
  pinnedTemplateCbId,
} from "../../utils/levelBands";
import "./TemplateApplyWizardDialog.css";

const STEP_HOW = 0;
const STEP_TARGET = 1;
const STEP_LEVEL = 2;
const STEP_FIRST_SLOT = 3;

function isTemplateCompetencySlot(item) {
  const n = String(item?.type_ex?.type_name_en || "").trim().toLowerCase();
  return n.includes("block") && n.includes("competency");
}

function sortedTemplateSlots(template) {
  return (template?.template_items || [])
    .map((item, rawIndex) => ({ item, rawIndex }))
    .sort(
      (a, b) =>
        (a.item?.tsti_order ?? a.rawIndex) - (b.item?.tsti_order ?? b.rawIndex) ||
        a.rawIndex - b.rawIndex
    )
    .map((row, sortedIndex) => ({ ...row, sortedIndex }));
}

export function pickableTemplateSlots(template) {
  return sortedTemplateSlots(template).filter(({ item }) => {
    if (isPersonalizedTypeName(item?.type_ex?.type_name_en)) return false;
    return Boolean(item?.type_id || item?.type_ex);
  });
}

export function templateSlotHeadline(item, lng) {
  const isCb = isTemplateCompetencySlot(item);
  return blockTypeHeadline(
    {
      type_ex: item?.type_ex,
      sub_type_ex: isCb ? null : item?.sub_type_ex,
      competency_block: isCb
        ? item?.competency_block || item?.blocks_compentency || item?.sub_type_ex
        : item?.competency_block,
      sequences: item?.sequences,
    },
    lng
  );
}

function usedCbIds(chosen) {
  return (chosen || []).map((c) => c.cb_id).filter(Boolean).map(String);
}

/** First main (non-intro) allowed block not used by an earlier slot; mirrors backend pickSlotCb. */
export function nextUnusedCb(allowed, chosen) {
  const main = (allowed || []).filter((cb) => cb.status !== "intro");
  if (!main.length) return allowed?.[0] || null;
  const used = new Set(usedCbIds(chosen));
  return main.find((cb) => !used.has(String(cb.cb_id))) || main[used.size % main.length];
}

async function enrichExercisesWithDetails(minimalList) {
  const result = [];
  for (const ex of minimalList || []) {
    try {
      const { data } = await exdetails({ ex_id: ex.ex_id });
      const full = data?.data;
      if (full) {
        result.push({
          ...full,
          ex_id: ex.ex_id,
          duration: ex.duration ?? full.te_duration ?? 10,
          cb_id: ex.cb_id,
          competency_blocks: ex.competency_blocks || full.competency_blocks,
        });
      } else {
        result.push({ ...ex, duration: ex.duration ?? 10 });
      }
    } catch {
      result.push({ ...ex, duration: ex.duration ?? 10 });
    }
  }
  return result;
}

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  };
}

/**
 * Same apply flow as planning: types only, or pick a catalogue exercise per type.
 * Both paths go through the level step so competency-block slots adapt to the
 * chosen level (group, group objectives, or a specific classement).
 */
export function TemplateApplyWizardDialog({
  open,
  template,
  tsId,
  grId,
  teId,
  applying = false,
  container,
  onClose,
  onBackToSelect,
  onConfirm,
  onError,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const lng = normalizeAppLang(i18n.language);

  const [step, setStep] = useState(STEP_HOW);
  const [typesOnlyPending, setTypesOnlyPending] = useState(false);
  const [targetType, setTargetType] = useState("group");
  const [targetId, setTargetId] = useState(null);
  const [choices, setChoices] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [groupPlayers, setGroupPlayers] = useState([]);
  const [tablesWithPlayers, setTablesWithPlayers] = useState([]);

  const [levelMode, setLevelMode] = useState("group");
  const [levelClassement, setLevelClassement] = useState("");
  const [levelPreview, setLevelPreview] = useState(null);
  const [loadingLevel, setLoadingLevel] = useState(false);

  const [slotInfo, setSlotInfo] = useState(null);
  const [slotCbId, setSlotCbId] = useState(null);
  const [slotNote, setSlotNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep(STEP_HOW);
    setTypesOnlyPending(false);
    setTargetType("group");
    setTargetId(grId);
    setChoices([]);
    setRecommendations([]);
    setLevelMode("group");
    setLevelClassement("");
    setLevelPreview(null);
    setSlotInfo(null);
    setSlotCbId(null);
    setSlotNote("");
    if (!grId) return;
    let cancelled = false;
    (async () => {
      try {
        const [playersRes, tablesRes] = await Promise.all([
          grpplayerlist({ gr_id: grId }),
          tablelist({ gr_id: grId, ts_id: tsId, te_id: teId }),
        ]);
        if (cancelled) return;
        setGroupPlayers(playersRes?.data?.data || []);
        const tablesData = tablesRes?.data?.data || [];
        setTablesWithPlayers(
          Array.isArray(tablesData)
            ? tablesData.filter((tbl) => (tbl.player1 || tbl.player2) && tbl.table_id)
            : []
        );
      } catch (e) {
        console.warn("Could not load players/tables for wizard:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, grId, tsId, teId]);

  const levelContext = useMemo(() => {
    if (levelMode === "specific") {
      return levelClassement ? { mode: "specific", classement: levelClassement } : { mode: "group" };
    }
    return { mode: levelMode };
  }, [levelMode, levelClassement]);

  // Level preview for the level step (group view; table / individual targets are resolved per slot).
  useEffect(() => {
    if (!open || step !== STEP_LEVEL || !grId) return;
    if (levelMode === "specific" && !levelClassement) {
      setLevelPreview(null);
      return;
    }
    let cancelled = false;
    setLoadingLevel(true);
    const params = new URLSearchParams({ gr_id: grId, mode: levelContext.mode });
    if (levelContext.classement) params.set("classement", levelContext.classement);
    axios
      .get(`${ip_back}api/level-bands/resolve?${params.toString()}`, { headers: authHeaders() })
      .then((res) => {
        if (!cancelled) setLevelPreview(res.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setLevelPreview(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingLevel(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, step, grId, levelMode, levelClassement, levelContext]);

  const cbNameById = useMemo(() => {
    const map = new Map();
    for (const row of [...(levelPreview?.allowed_cbs || []), ...(levelPreview?.blocked_cbs || [])]) {
      map.set(String(row.cb_id), row.name);
    }
    return map;
  }, [levelPreview]);

  const fetchSlot = useCallback(
    async (slot, chosen, cbId) => {
      const item = slot?.item;
      const exclude = (chosen || []).map((c) => c.ex_id).filter(Boolean);
      const isCb = isTemplateCompetencySlot(item);
      if (isCb) {
        const body = {
          ts_id: tsId,
          gr_id: grId,
          language: lng,
          target_type: targetType,
          target_id: targetType === "group" ? grId : targetId,
          slot_index: slot.sortedIndex,
          exclude_ex_ids: exclude,
          level_context: levelContext,
          slot_role: item?.tsti_slot_role || undefined,
          intro_used: (chosen || []).some((c) => c.intro),
          used_cb_ids: usedCbIds(chosen),
        };
        if (cbId) body.cb_id = cbId;
        if (targetType === "table") body.te_id = teId;
        const res = await axios.post(
          `${ip_back}api/exercises/recommend-competency-blocks-for-session`,
          body,
          { headers: authHeaders() }
        );
        return { isCb, data: res.data?.data || {}, exclude };
      }
      const url =
        targetType === "individual"
          ? `${ip_back}api/exercises/recommend-for-player`
          : `${ip_back}api/exercises/recommend-for-session`;
      const body = {
        ts_id: tsId,
        gr_id: grId,
        language: lng,
        type_id: item.type_id,
        sub_type_id: item.sub_type_id || undefined,
        ...(targetType === "individual" ? { player_id: targetId } : {}),
      };
      const res = await axios.post(url, body, { headers: authHeaders() });
      return { isCb, data: { exercises: res.data?.data?.exercises || [] }, exclude };
    },
    [tsId, grId, lng, targetType, targetId, teId, levelContext]
  );

  const showExercises = async (data, exclude) => {
    const filtered = (data.exercises || []).filter(
      (row) => row?.ex_id && !exclude.includes(row.ex_id)
    );
    const enriched = await enrichExercisesWithDetails(filtered);
    setRecommendations(enriched);
    return enriched;
  };

  const openSlot = async (slot, chosen) => {
    setRecommendations([]);
    setSlotInfo(null);
    setSlotCbId(null);
    setSlotNote("");
    setLoadingRecs(true);
    try {
      const pinned = pinnedTemplateCbId(slot?.item);
      let { isCb, data, exclude } = await fetchSlot(slot, chosen, pinned);
      if (isCb) {
        const allowed = data.allowed_cbs || [];
        // Template pinned a block this level does not work: adapt to the next block
        // earlier slots have not used yet.
        if (pinned && data.cb_allowed === false && allowed.length > 0) {
          const replacement = nextUnusedCb(allowed, chosen);
          ({ data } = await fetchSlot(slot, chosen, replacement.cb_id));
          setSlotNote(t("levelBlockReplaced", { name: replacement.name }));
        } else if (data.slot_fallback && allowed[0]) {
          setSlotNote(t("levelSlotFallback", { name: allowed[0].name }));
        }
        setSlotInfo(data);
        setSlotCbId(data.selected_cb_id || allowed[0]?.cb_id || null);
      }
      const enriched = await showExercises(data, exclude);
      if (!enriched.length && !isCb) {
        onError?.(t("noRecommendations") || "No recommendations");
      }
    } catch (err) {
      onError?.(
        err?.response?.data?.message ||
          err?.message ||
          t("errorLoadingRecommendations")
      );
    } finally {
      setLoadingRecs(false);
    }
  };

  const slots = pickableTemplateSlots(template);
  const pickIndex = step - STEP_FIRST_SLOT;
  const currentSlot = slots[pickIndex];

  const selectSlotCb = async (cbId) => {
    if (!currentSlot || !cbId || String(cbId) === String(slotCbId)) return;
    setSlotCbId(cbId);
    setSlotNote("");
    setRecommendations([]);
    setLoadingRecs(true);
    try {
      const { data, exclude } = await fetchSlot(currentSlot, choices, cbId);
      setSlotInfo(data);
      await showExercises(data, exclude);
    } catch (err) {
      onError?.(err?.response?.data?.message || err?.message || t("errorLoadingRecommendations"));
    } finally {
      setLoadingRecs(false);
    }
  };

  const handleTypesOnly = () => {
    setTypesOnlyPending(true);
    setStep(STEP_LEVEL);
  };

  const handleLevelNext = async () => {
    if (typesOnlyPending) {
      onConfirm?.({ typesOnly: true, levelContext });
      return;
    }
    if (slots.length === 0) {
      onConfirm?.({ typesOnly: false, cbSlotChoices: null, levelContext });
      return;
    }
    setChoices([]);
    setStep(STEP_FIRST_SLOT);
    await openSlot(slots[0], []);
  };

  const advance = async (newChoices) => {
    setChoices(newChoices);
    if (pickIndex >= slots.length - 1) {
      onConfirm?.({ typesOnly: false, cbSlotChoices: newChoices, levelContext });
      return;
    }
    setStep((s) => s + 1);
    await openSlot(slots[pickIndex + 1], newChoices);
  };

  const isIntroCb = (cbId) =>
    !!cbId &&
    (slotInfo?.allowed_cbs || []).some((cb) => cb.status === "intro" && String(cb.cb_id) === String(cbId));

  const handlePickExercise = async (ex) => {
    if (!currentSlot) return;
    const currentIdx = currentSlot.sortedIndex;
    const chosenCbId = slotCbId || ex.cb_id || pinnedTemplateCbId(currentSlot.item) || null;
    const newChoices = [
      ...choices.filter((c) => c.itemIndex !== currentIdx),
      {
        itemIndex: currentIdx,
        ex_id: ex.ex_id,
        cb_id: chosenCbId,
        ...(isIntroCb(chosenCbId) ? { intro: true } : {}),
      },
    ].sort((a, b) => a.itemIndex - b.itemIndex);
    await advance(newChoices);
  };

  const handleKeepType = async () => {
    if (!currentSlot) return;
    const newChoices = [
      ...choices.filter((c) => c.itemIndex !== currentSlot.sortedIndex),
      {
        itemIndex: currentSlot.sortedIndex,
        defer: true,
        ...(slotCbId ? { cb_id: slotCbId } : {}),
        ...(isIntroCb(slotCbId) ? { intro: true } : {}),
      },
    ];
    await advance(newChoices);
  };

  const handleBack = () => {
    if (step === STEP_LEVEL && typesOnlyPending) {
      setTypesOnlyPending(false);
      setStep(STEP_HOW);
      return;
    }
    setStep((s) => Math.max(STEP_HOW, s - 1));
  };

  const title =
    step === STEP_HOW
      ? t("templateApplyHow")
      : step === STEP_TARGET
        ? t("templateApplyTarget")
        : step === STEP_LEVEL
          ? t("templateApplyLevel")
          : `${t("templateApplySelectExercise")} ${pickIndex + 1} / ${slots.length}`;

  const previewLevel = levelPreview?.level;
  const objectiveIds =
    levelMode === "objectives"
      ? previewLevel?.objective_cb_ids || []
      : previewLevel?.group_objective_cb_ids || [];

  const renderLevelStep = () => (
    <div className="template-apply-level">
      <p className="template-apply-level__hint">{t("templateApplyLevelHint")}</p>
      <RadioGroup value={levelMode} onChange={(e) => setLevelMode(e.target.value)}>
        <FormControlLabel
          value="group"
          control={<Radio />}
          label={
            <span className="template-apply-level__option">
              <strong>{t("levelModeGroup")}</strong>
              <span>{t("levelModeGroupHint")}</span>
            </span>
          }
        />
        <FormControlLabel
          value="objectives"
          control={<Radio />}
          label={
            <span className="template-apply-level__option">
              <strong>{t("levelModeObjectives")}</strong>
              <span>{t("levelModeObjectivesHint")}</span>
            </span>
          }
        />
        <FormControlLabel
          value="specific"
          control={<Radio />}
          label={
            <span className="template-apply-level__option">
              <strong>{t("levelModeSpecific")}</strong>
              <span>{t("levelModeSpecificHint")}</span>
            </span>
          }
        />
      </RadioGroup>

      {levelMode === "specific" ? (
        <FormControl size="small" sx={{ mt: 1, ml: 4, minWidth: 160 }}>
          <InputLabel>{t("levelSelectClassement")}</InputLabel>
          <Select
            value={levelClassement}
            label={t("levelSelectClassement")}
            onChange={(e) => setLevelClassement(e.target.value)}
          >
            {CLASSEMENTS.map((c) => (
              <MenuItem key={c} value={c}>
                {c === "UNRANKED" ? t("levelUnranked") : c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}

      <div className="template-apply-level__summary" aria-live="polite">
        {loadingLevel ? (
          <span>{t("loading")}</span>
        ) : previewLevel ? (
          <>
            {targetType !== "group" && levelMode !== "specific" ? (
              <span className="template-apply-level__note">{t("levelPlayersLevel")}</span>
            ) : (
              <Chip color="primary" label={levelSummaryLabel(previewLevel, t)} />
            )}
            {levelMode === "objectives" ? (
              objectiveIds.length ? (
                <div className="template-apply-level__chips">
                  {objectiveIds.map((id) => (
                    <Chip key={id} size="small" variant="outlined" label={cbNameById.get(String(id)) || id} />
                  ))}
                </div>
              ) : (
                <span className="template-apply-level__note">{t("levelNoObjectives")}</span>
              )
            ) : null}
          </>
        ) : null}
      </div>

      {previewLevel?.multi_band && levelMode !== "specific" ? (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={
            onBackToSelect ? (
              <Button color="inherit" size="small" onClick={() => onBackToSelect?.()}>
                {t("levelPickLevelBandsLayout")}
              </Button>
            ) : null
          }
        >
          {t("levelMultiBandWarning", {
            from: previewLevel.spread?.weakest === "UNRANKED" ? t("levelUnranked") : previewLevel.spread?.weakest,
            to: previewLevel.spread?.strongest,
            bands: (previewLevel.spread?.bands || []).map((b) => t(`levelBand_${b}`)).join(" · "),
          })}
        </Alert>
      ) : null}
    </div>
  );

  const renderSlotCbChips = () => {
    if (!slotInfo) return null;
    const allowed = slotInfo.allowed_cbs || [];
    const blocked = slotInfo.blocked_cbs || [];
    return (
      <div className="template-apply-level__blocks">
        <span className="template-apply-level__label">{t("levelAllowedBlocks")}</span>
        <div className="template-apply-level__chips">
          {allowed.map((cb) => (
            <Chip
              key={cb.cb_id}
              label={cb.status === "intro" ? `${cb.name} · ${t("levelStatus_intro")}` : cb.name}
              className={cb.status === "intro" ? "template-apply-level__chip--intro" : undefined}
              color={String(cb.cb_id) === String(slotCbId) ? "primary" : "default"}
              variant={String(cb.cb_id) === String(slotCbId) ? "filled" : "outlined"}
              onClick={() => selectSlotCb(cb.cb_id)}
              disabled={loadingRecs}
              title={t(`levelStatusHint_${cb.status}`)}
            />
          ))}
          {blocked
            .filter((cb) => cb.reason !== "slot_role")
            .map((cb) => (
              <Tooltip key={cb.cb_id} title={blockedReasonLabel(cb, t)}>
                <span>
                  <Chip label={cb.name} variant="outlined" disabled />
                </span>
              </Tooltip>
            ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose?.()}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      container={container || undefined}
      disableScrollLock={Boolean(container)}
      sx={container ? { zIndex: 5000 } : undefined}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {step === STEP_HOW ? (
          <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
            <button
              type="button"
              className="template-apply-choice"
              onClick={handleTypesOnly}
              disabled={applying}
            >
              <strong>{t("templateApplyTypesOnly")}</strong>
              <span>{t("templateApplyTypesOnlyHint")}</span>
            </button>
            <button
              type="button"
              className="template-apply-choice template-apply-choice--primary"
              onClick={() => setStep(STEP_TARGET)}
              disabled={applying}
            >
              <strong>{t("templateApplyPickExercises")}</strong>
              <span>{t("templateApplyPickExercisesHint")}</span>
            </button>
          </div>
        ) : step === STEP_TARGET ? (
          <div style={{ marginTop: "12px" }}>
            <p style={{ marginBottom: "12px", color: "#555" }}>
              {t("templateApplyTargetHint")}
            </p>
            <RadioGroup
              value={targetType}
              onChange={(e) => {
                setTargetType(e.target.value);
                if (e.target.value === "group") setTargetId(grId);
                else if (e.target.value === "individual" && groupPlayers?.[0]) {
                  setTargetId(groupPlayers[0].player_id);
                } else if (e.target.value === "table" && tablesWithPlayers?.[0]) {
                  setTargetId(tablesWithPlayers[0].table_id);
                }
              }}
            >
              <FormControlLabel value="group" control={<Radio />} label={t("targetGroup")} />
              <FormControlLabel value="individual" control={<Radio />} label={t("targetIndividual")} />
              {targetType === "individual" && (
                <FormControl size="small" fullWidth sx={{ mt: 1, ml: 3 }}>
                  <InputLabel>{t("selectPlayer")}</InputLabel>
                  <Select
                    value={targetId || ""}
                    label={t("selectPlayer")}
                    onChange={(e) => setTargetId(e.target.value)}
                  >
                    {groupPlayers.map((p) => (
                      <MenuItem key={p.player_id} value={p.player_id}>
                        {p.user_alias ||
                          `${p.user_first_name || ""} ${p.user_last_name || ""}`.trim() ||
                          p.player_id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControlLabel value="table" control={<Radio />} label={t("targetTable")} />
              {targetType === "table" && (
                <FormControl size="small" fullWidth sx={{ mt: 1, ml: 3 }}>
                  <InputLabel>{t("selectTable")}</InputLabel>
                  <Select
                    value={targetId || ""}
                    label={t("selectTable")}
                    onChange={(e) => setTargetId(e.target.value)}
                  >
                    {tablesWithPlayers.map((tbl) => (
                      <MenuItem key={tbl.table_id} value={tbl.table_id}>
                        {tbl.table_description || `Table ${tbl.table_number}`} –{" "}
                        {[tbl.player1?.user_alias, tbl.player2?.user_alias]
                          .filter(Boolean)
                          .join(" / ") || "-"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </RadioGroup>
          </div>
        ) : step === STEP_LEVEL ? (
          renderLevelStep()
        ) : (
          <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "12px" }}>
            <div className="template-apply-level__slot-head">
              <p style={{ margin: 0, fontWeight: 750 }}>
                {templateSlotHeadline(currentSlot?.item, lng)}
              </p>
              {slotInfo?.level ? (
                <Chip size="small" color="primary" variant="outlined" label={levelSummaryLabel(slotInfo.level, t)} />
              ) : null}
            </div>
            {renderSlotCbChips()}
            {slotNote ? <Alert severity="info">{slotNote}</Alert> : null}
            {slotInfo?.selected_is_intro ? <Alert severity="info">{t("levelIntroNote")}</Alert> : null}
            {loadingRecs ? (
              <div style={{ textAlign: "center", padding: "24px" }}>{t("loading")}</div>
            ) : recommendations.length === 0 ? (
              <Button variant="outlined" onClick={handleKeepType}>
                {t("templateApplyKeepType")}
              </Button>
            ) : (
              recommendations.map((ex) => (
                <ExerciseCard
                  key={ex.ex_id}
                  exercise={ex}
                  isSelected={choices.some(
                    (c) => c.ex_id === ex.ex_id && c.itemIndex === currentSlot?.sortedIndex
                  )}
                  onSelect={() => handlePickExercise(ex)}
                  lng={lng}
                  t={t}
                  selectMode="single"
                  showDurationInput={false}
                />
              ))
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {step > STEP_HOW ? (
          <Button onClick={handleBack} style={{ color: "#666" }}>
            {t("back") || "Back"}
          </Button>
        ) : null}
        <Button
          onClick={() => onBackToSelect?.()}
          style={{ color: "#666" }}
        >
          {t("cancel")}
        </Button>
        {step === STEP_TARGET ? (
          <Button
            variant="contained"
            onClick={() => setStep(STEP_LEVEL)}
            disabled={
              applying ||
              (targetType === "individual" && !targetId) ||
              (targetType === "table" && (!targetId || tablesWithPlayers.length === 0))
            }
          >
            {t("next")}
          </Button>
        ) : null}
        {step === STEP_LEVEL ? (
          <Button
            variant="contained"
            onClick={handleLevelNext}
            disabled={applying || (levelMode === "specific" && !levelClassement)}
          >
            {typesOnlyPending ? t("templateApplyTypesOnly") : t("next")}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

export default TemplateApplyWizardDialog;
